import { Injectable } from '@angular/core';
import * as _ from 'lodash';

import { Presentation } from 'src/app/api/services/presentation';

export type HistoryEntry = {
  sequenceId: number;
  presentation: Presentation;
  branding: any;
};

const MAX_UNDO_STACK_SIZE = 50;

@Injectable({
  providedIn: 'root',
})
export class HistoryService {
  isRestoring = false;

  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private sequenceCounter = 0;

  private cloneEntry(entry: HistoryEntry): HistoryEntry {
    return {
      sequenceId: entry.sequenceId,
      presentation: JSON.parse(JSON.stringify(entry.presentation)),
      branding: JSON.parse(JSON.stringify(entry.branding)),
    };
  }

  push(presentation: Presentation, branding: any): void {
    const clonedPresentation = JSON.parse(JSON.stringify(presentation));
    const clonedBranding = JSON.parse(JSON.stringify(branding));

    const current = this.undoStack[this.undoStack.length - 1];
    if (
      current &&
      _.isEqual(current.presentation, clonedPresentation) &&
      _.isEqual(current.branding, clonedBranding)
    ) {
      return;
    }

    const snapshot: HistoryEntry = {
      sequenceId: ++this.sequenceCounter,
      presentation: clonedPresentation,
      branding: clonedBranding,
    };

    this.undoStack.push(snapshot);

    if (this.undoStack.length > MAX_UNDO_STACK_SIZE) {
      this.undoStack = this.undoStack.slice(
        this.undoStack.length - MAX_UNDO_STACK_SIZE,
      );
    }

    this.redoStack = [];
  }

  undo(): HistoryEntry | null {
    if (!this.canUndo()) {
      return null;
    }

    const current = this.undoStack.pop();
    this.redoStack.push(current);

    const previous = this.undoStack[this.undoStack.length - 1];
    return previous ? this.cloneEntry(previous) : null;
  }

  redo(): HistoryEntry | null {
    if (!this.canRedo()) {
      return null;
    }

    const next = this.redoStack.pop();
    this.undoStack.push(next);

    return this.cloneEntry(next);
  }

  canUndo(): boolean {
    return this.undoStack.length > 1;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  getCurrentSequenceId(): number {
    return this.sequenceCounter;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  reset(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.sequenceCounter = 0;
  }
}
